import { useGT } from 'gt-react';
import type { WorkbookColumnarSheet } from '@erp/shared-types';
import type { ColumnType, SheetPlan } from '@erp/workbooks';

type SortDirection = WorkbookColumnarSheet['sort'][number]['direction'];

/**
 * The Declared type a Worksheet's filters and sort see for a Field: from the
 * Plan when there is one, else the Output column's type. Anything else is Text,
 * as every Source column is.
 */
export function worksheetFieldType(
  sheet: WorkbookColumnarSheet,
  name: string,
  sheetPlan?: SheetPlan,
): ColumnType {
  return (
    sheetPlan?.stages?.fields.find((field) => field.name === name)?.type ??
    sheet.columns.find((column) => column.name === name)?.type ??
    'text'
  );
}

/** The Declared types in Excel's names, for type pickers. */
export function useColumnTypeOptions(): {
  value: ColumnType;
  label: string;
}[] {
  const gt = useGT();
  return [
    { value: 'text', label: gt('Text', { context: 'worksheet data type' }) },
    {
      value: 'decimal',
      label: gt('Number', { context: 'worksheet data type' }),
    },
    { value: 'date', label: gt('Date', { context: 'worksheet data type' }) },
    {
      value: 'datetime',
      label: gt('Date and time', { context: 'worksheet data type' }),
    },
    {
      value: 'boolean',
      label: gt('TRUE/FALSE', { context: 'worksheet data type' }),
    },
  ];
}

/** Names a sort direction as Excel's Sort does for the Field's type: "A to Z", "Smallest to largest"… */
export function useSortDirectionLabel(): (
  type: ColumnType,
  direction: SortDirection,
) => string {
  const gt = useGT();
  const context = 'worksheet sort order for a column type';
  const labels: Record<ColumnType, Record<SortDirection, string>> = {
    text: {
      asc: gt('A to Z', { context }),
      desc: gt('Z to A', { context }),
    },
    decimal: {
      asc: gt('Smallest to largest', { context }),
      desc: gt('Largest to smallest', { context }),
    },
    date: {
      asc: gt('Oldest to newest', { context }),
      desc: gt('Newest to oldest', { context }),
    },
    datetime: {
      asc: gt('Oldest to newest', { context }),
      desc: gt('Newest to oldest', { context }),
    },
    boolean: {
      asc: gt('FALSE to TRUE', { context }),
      desc: gt('TRUE to FALSE', { context }),
    },
  };
  return (type, direction) => labels[type][direction];
}
